<template>
  <div class="stc">
    <StickyHeader
      v-if="isDesktop"
      ref="stickyHeaderRef"
      :anchor-ref="tabsContainerRef"
      :force-hide="!isCollectionInView"
      class="stc__header"
      data-testid="sticky-header"
      @sticky-change="isHeaderSticky = $event"
    >
      <div class="content grid-container padding-top-s padding-bottom-s">
        <button type="button" class="h3 stc__header-title-btn" :disabled="!isHeaderSticky" @click="scrollToTop">
          {{ brandName }} - {{ productExclusiveName || productName }}
        </button>
        <div v-if="tabs.length <= 1" class="body-large margin-top-xxs">
          {{ activeTabMembers.length }} item{{ pluralize(activeTabMembers.length) }}
        </div>
        <Tabs v-else :model-value="activeTab" :tabs="tabs" class="stc__tabs" @update:model-value="handleTabChange">
          <template #tab-item="{ tab }">
            {{ tab }} <span>({{ memberCountByGroup[tab] }})</span>
          </template>
        </Tabs>
      </div>
    </StickyHeader>

    <div ref="tabsContainerRef" class="stc__tabs-anchor">
      <ImageGallery>
        <template #product-short-summary="{ isFirstSlideActive, index }">
          <FitParentOverlay v-if="index === 0" :class="['stc__overlay', { hidden: !isFirstSlideActive }]">
            <ProductShortSummary />
          </FitParentOverlay>
        </template>
      </ImageGallery>
    </div>

    <Tabs v-if="tabs.length > 1" :model-value="activeTab" :tabs="tabs" class="stc__tabs" @update:model-value="handleTabChange">
      <template #tab-item="{ tab }">
        {{ tab }} <span>({{ memberCountByGroup[tab] }})</span>
      </template>
    </Tabs>

    <section ref="layoutRef" class="stc__layout" :class="{ 'stc__layout--no-tabs': tabs.length <= 1 }" aria-label="Collection Members">
      <div class="stc__main">
        <ShopTheCollectionMemberList :list="activeTabMemberIds">
          <template #item="{ itemId }">
            <CompactProduct :product-id="itemId" />
            <Product v-if="!isMobile && !isTablet" :product-id="itemId" />
          </template>
        </ShopTheCollectionMemberList>
      </div>

      <SummaryBar :summary-bar-top-offset="summaryBarTopOffset" />
    </section>

    <!-- This container is outside the flex layout, allowing items teleported here to be centered relative to the page -->
    <div id="stc-footer-actions" class="stc__footer-actions"></div>

    <FirstTimeMessageSheet v-if="!isDesktop" />

    <MemberDetailsSheet v-if="activeBottomSheetProductId" :product-id="activeBottomSheetProductId" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useElementSize, useIntersectionObserver } from '@vueuse/core';

import type { ComponentPublicInstance } from 'vue';

import ImageGallery from '~/components/shopTheCollection/ImageGallery.vue';
import FitParentOverlay from '~/components/common/FitParentOverlay.vue';
import Tabs from '~/components/common/Tabs.vue';
import ProductShortSummary from '~/components/shopTheCollection/ProductShortSummary.vue';
import StickyHeader from '~/components/common/StickyHeader.vue';
import SummaryBar from '~/components/shopTheCollection/SummaryBar.vue';
import ShopTheCollectionMemberList from '~/components/shopTheCollection/member/List.vue';
import Product from '~/components/shopTheCollection/member/Product.vue';
import CompactProduct from '~/components/shopTheCollection/member/CompactProduct.vue';
import MemberDetailsSheet from '~/components/shopTheCollection/member/MemberDetailsSheet.vue';
import FirstTimeMessageSheet from '~/components/shopTheCollection/FirstTimeMessageSheet.vue';

import { useProduct } from '~/modules/product/composables/useProduct';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useShopTheCollectionTabsStore } from '~/stores/useShopTheCollectionTabsStore';
import { useShopTheCollectionUIStore } from '~/stores/useShopTheCollectionUIStore';
import { pluralize } from '~/utils/string';
import { tagSTCFilterClick } from '~/features/analytics/shopTheCollection';

const store = useShopTheCollectionTabsStore();
const { tabs, activeTab, memberCountByGroup, activeTabMembers, activeTabMemberIds } = storeToRefs(store);

const uiStore = useShopTheCollectionUIStore();
const { activeBottomSheetProductId } = storeToRefs(uiStore);

const { productData } = useProduct();
const { init, setActiveTab } = store;

const { brandName, productExclusiveName, productName } = useCommonProductGetters();

init(productData);

const tabsContainerRef = ref<HTMLElement | null>(null);
const layoutRef = ref<HTMLElement | null>(null);

const stickyHeaderRef = ref<ComponentPublicInstance | null>(null);
const isHeaderSticky = ref(false);
const isCollectionInView = ref(true);

const { height: stickyHeaderHeight } = useElementSize(stickyHeaderRef);
const { isMobile, isTablet, isDesktop } = useDeviceType();

const isHeaderVisible = computed(() => isHeaderSticky.value && isCollectionInView.value);

useIntersectionObserver(
  layoutRef,
  ([{ isIntersecting, boundingClientRect }]) => {
    // We only care if it's scrolled PAST the top.
    // If it's not intersecting and bottom is <= 0, it means it's above the viewport.
    isCollectionInView.value = isIntersecting || boundingClientRect.bottom > 0;
  },
  { threshold: 0 },
);

const summaryBarTopOffset = computed(() => {
  // 24px - summaryBar margin-top
  return isHeaderVisible.value ? stickyHeaderHeight.value + 24 : 0;
});

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

/**
 * Handles tab changes by updating store state and scrolling to the member list.
 */
const handleTabChange = (newTab: string) => {
  setActiveTab(newTab);
  tagSTCFilterClick();

  if (layoutRef.value && !isMobile.value) {
    const scrollOffset = stickyHeaderHeight.value || 0;
    const elementPosition = layoutRef.value.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - scrollOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.stc {
  .stc__header {
    background-color: $white;
    border-bottom: 1px solid $gray-5-color;

    .stc__header-title-btn {
      cursor: pointer;
    }
  }

  &:deep(.image-gallery) {
    @include breakpoint(small only) {
      // Negative margins are used to occupy full screen width despite parent paddings
      margin-right: -16px;
      margin-left: -16px;
    }
  }

  .stc__overlay {
    background: $stc-overlay-mobile-gradient;
    pointer-events: none;
    opacity: 1;
    transition:
      opacity $stc-overlay-transition-duration $stc-overlay-transition-timing,
      visibility $stc-overlay-transition-duration $stc-overlay-transition-timing;

    &.hidden {
      opacity: 0;
      visibility: hidden;
    }

    @include breakpoint(medium up) {
      background: $stc-overlay-desktop-gradient;
    }
  }

  .stc__tabs {
    @include margin-top-xs();
    gap: 8px;

    @include breakpoint(small only) {
      // Negative margins are used to occupy full screen width despite parent paddings
      margin-right: -16px;
      margin-left: -16px;

      &:deep(.tab-button) {
        &:first-child {
          margin-left: 16px;
        }

        &:last-child {
          margin-right: 16px;
        }
      }
    }

    @include breakpoint(large up) {
      @include margin-top-s();
      gap: 16px;
    }

    :deep(.tab-button) {
      padding: 10px 8px;
      border: 1px solid $gray-1-color;
      cursor: pointer;

      &.tab-button--active {
        border: 2px solid $black;
      }

      @include breakpoint(large up) {
        padding-right: 12px;
        padding-left: 12px;
      }
    }
  }

  .stc__layout {
    @include margin-top-xs();
    border-top: 1px solid $gray-5-color;
    display: flex;
    flex-direction: column;
    margin-left: -16px;
    margin-right: -16px;

    @include breakpoint(large up) {
      @include margin-left-z();
      @include margin-right-z();
      @include margin-top-s();
      @include padding-top-s();
      flex-direction: row;
      align-items: flex-start;
      gap: clamp(24px, 10vw, 146px);
    }
  }

  .stc__layout--no-tabs {
    border-top: 0;

    @include breakpoint(small only) {
      @include margin-top-z;
    }
  }

  .stc__main {
    flex: 1 1 auto;
    min-width: 0;
  }

  .stc__footer-actions {
    display: flex;
    justify-content: center;
    width: 100%;
  }
}
</style>
